import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Mail, MessageCircle, Send, Map, Car, ArrowLeft } from 'lucide-react';
import {
  QuoteFormData,
  FullTripFormData,
  DriverOnlyFormData,
  TravelStatus,
  TravelStyle,
  BudgetStyle,
  CommunicationMethod,
  ServiceType,
  VehicleType,
  TRAVEL_STYLES,
  BUDGET_STYLES,
  LANGUAGE_OPTIONS,
  TERMS_AND_CONDITIONS,
  VEHICLE_TYPES,
  PLACES_TO_VISIT,
  ACTIVITIES_TO_DO
} from '../../types/quote';

interface QuoteRequestFormProps {
  tripId?: string;
  tripName?: string;
  tripTagline?: string;
  startDate?: Date | string;
  endDate?: Date | string;
  numAdults?: number;
  numChildren?: number;
  isLazyMode?: boolean;
  onClose: () => void;
  onSubmit: (formData: QuoteFormData) => Promise<void>;
}

const QuoteRequestForm: React.FC<QuoteRequestFormProps> = ({
  tripId,
  tripName,
  tripTagline,
  startDate,
  endDate,
  numAdults = 2,
  numChildren = 0,
  isLazyMode = false,
  onClose,
  onSubmit
}) => {
  // Convert dates to Date objects if they're strings
  const startDateObj = startDate ? (typeof startDate === 'string' ? new Date(startDate) : startDate) : new Date();
  const endDateObj = endDate ? (typeof endDate === 'string' ? new Date(endDate) : endDate) : new Date();

  // Format dates for input
  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };
  
  // Get min date for lazy mode
  const getMinDate = () => {
    const today = new Date();
    if (travelStatus === 'visiting_soon') {
      // 2 days from today
      today.setDate(today.getDate() + 2);
    } else {
      // Tomorrow
      today.setDate(today.getDate() + 1);
    }
    return today.toISOString().split('T')[0];
  };

  // Service type selection state
  const [serviceType, setServiceType] = useState<ServiceType | null>(null);
  const [showServiceSelection, setShowServiceSelection] = useState(true);

  // Common form state
  const [travelStatus, setTravelStatus] = useState<TravelStatus>('visiting_soon');
  const [arrivalDate, setArrivalDate] = useState(isLazyMode ? '' : formatDateForInput(startDateObj));
  const [arrivalTime, setArrivalTime] = useState('09:00');
  const [departureDate, setDepartureDate] = useState(isLazyMode ? '' : formatDateForInput(endDateObj));
  const [departureTime, setDepartureTime] = useState('18:00');
  const [pickupTime, setPickupTime] = useState('09:00');
  const [dropoffTime, setDropoffTime] = useState('18:00');

  // Get today's date for min date validation
  const today = new Date().toISOString().split('T')[0];
  
  // Lazy mode specific state
  const [placesToVisit, setPlacesToVisit] = useState<string[]>([]);
  const [showOtherPlaces, setShowOtherPlaces] = useState(false);
  const [otherPlacesText, setOtherPlacesText] = useState('');
  const [activitiesToDo, setActivitiesToDo] = useState<string[]>([]);
  const [showOtherActivities, setShowOtherActivities] = useState(false);
  const [otherActivitiesText, setOtherActivitiesText] = useState('');
  const [roughItinerary, setRoughItinerary] = useState('');
  
  // Full trip form state
  const [travelStyle, setTravelStyle] = useState<TravelStyle | ''>('');
  const [budgetStyle, setBudgetStyle] = useState<BudgetStyle | ''>('');
  const [languagePreference, setLanguagePreference] = useState('en');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [termsAccepted, setTermsAccepted] = useState<[boolean, boolean, boolean]>([false, false, false]);
  const [communicationMethod, setCommunicationMethod] = useState<CommunicationMethod | ''>('');

  // Driver-only form state
  const [adults, setAdults] = useState(numAdults);
  const [children, setChildren] = useState(numChildren);
  const [infants, setInfants] = useState(0);
  const [vehicleType, setVehicleType] = useState<VehicleType | ''>('');
  const [hasSpecialNeeds, setHasSpecialNeeds] = useState(false);
  const [specialNeedsDetails, setSpecialNeedsDetails] = useState('');
  const [driverLanguagePreference, setDriverLanguagePreference] = useState('en');
  const [driverAdditionalNotes, setDriverAdditionalNotes] = useState('');
  const [driverTermsAccepted, setDriverTermsAccepted] = useState(false);
  const [driverCommunicationMethod, setDriverCommunicationMethod] = useState<CommunicationMethod | ''>('');

  // UI state
  const [expandedTravelStyle, setExpandedTravelStyle] = useState<string | null>(null);
  const [expandedBudgetStyle, setExpandedBudgetStyle] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation
  const isFormValid = () => {
    if (serviceType === 'full_trip') {
      if (!travelStyle || !budgetStyle || !communicationMethod) return false;
      // For lazy mode, only require the last term (index 2)
      if (isLazyMode) {
        if (!termsAccepted[2]) return false;
      } else {
        if (!termsAccepted[0] || !termsAccepted[1] || !termsAccepted[2]) return false;
      }
      
      if (travelStatus === 'visiting_soon') {
        if (!arrivalDate || !arrivalTime || !departureDate || !departureTime) return false;
      } else {
        if (isLazyMode && (!arrivalDate || !departureDate)) return false;
        if (!pickupTime || !dropoffTime) return false;
      }
      
      // Lazy mode validations
      if (isLazyMode) {
        if (placesToVisit.length === 0 && !otherPlacesText.trim()) return false;
        if (activitiesToDo.length === 0 && !otherActivitiesText.trim()) return false;
      }
    } else if (serviceType === 'driver_only') {
      if (!vehicleType || !driverCommunicationMethod) return false;
      if (!driverTermsAccepted) return false;
      if (adults + children + infants < 1) return false;
      if (hasSpecialNeeds && !specialNeedsDetails.trim()) return false;
      
      if (travelStatus === 'visiting_soon') {
        if (!arrivalDate || !arrivalTime || !departureDate || !departureTime) return false;
      } else {
        if (isLazyMode && (!arrivalDate || !departureDate)) return false;
        if (!pickupTime || !dropoffTime) return false;
      }
      
      // Lazy mode validation for driver
      if (isLazyMode && !roughItinerary.trim()) return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setError('Please fill in all required fields and accept all terms');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let formData: QuoteFormData;

      if (serviceType === 'full_trip') {
        formData = {
          serviceType: 'full_trip',
          ...(isLazyMode && {
            isLazy: true,
            tripName: `Quick Quote - ${new Date().toLocaleDateString()}`
          }),
          travelStatus,
          arrivalDate: isLazyMode ? arrivalDate : formatDateForInput(startDateObj),
          departureDate: isLazyMode ? departureDate : formatDateForInput(endDateObj),
          ...(travelStatus === 'visiting_soon' ? {
            arrivalTime,
            departureTime
          } : {
            pickupTime,
            dropoffTime
          }),
          travelers: {
            adults,
            children,
            infants
          },
          ...(isLazyMode && {
            placesToVisit,
            otherPlaces: otherPlacesText,
            activitiesToDo,
            otherActivities: otherActivitiesText
          }),
          travelStyle: travelStyle as TravelStyle,
          budgetStyle: budgetStyle as BudgetStyle,
          languagePreference,
          additionalNotes,
          termsAccepted,
          communicationMethod: communicationMethod as CommunicationMethod
        } as FullTripFormData;
      } else {
        formData = {
          serviceType: 'driver_only',
          ...(isLazyMode && {
            isLazy: true,
            tripName: `Quick Quote - ${new Date().toLocaleDateString()}`
          }),
          travelStatus,
          arrivalDate: isLazyMode ? arrivalDate : formatDateForInput(startDateObj),
          ...(travelStatus === 'visiting_soon' ? {
            arrivalTime,
            departureTime
          } : {
            pickupTime,
            dropoffTime
          }),
          departureDate: isLazyMode ? departureDate : formatDateForInput(endDateObj),
          travelers: {
            adults,
            children,
            infants
          },
          vehicleType: vehicleType as VehicleType,
          specialNeeds: {
            hasSpecialNeeds,
            details: hasSpecialNeeds ? specialNeedsDetails : undefined
          },
          ...(isLazyMode && {
            roughItinerary
          }),
          additionalNotes: driverAdditionalNotes,
          languagePreference: driverLanguagePreference,
          termsAccepted: driverTermsAccepted,
          communicationMethod: driverCommunicationMethod as CommunicationMethod
        } as DriverOnlyFormData;
      }

      await onSubmit(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to submit quote request');
      setIsSubmitting(false);
    }
  };

  const handleTermsChange = (index: 0 | 1 | 2) => {
    const newTerms: [boolean, boolean, boolean] = [...termsAccepted];
    newTerms[index] = !newTerms[index];
    setTermsAccepted(newTerms);
  };

  const handleServiceTypeSelect = (type: ServiceType) => {
    setServiceType(type);
    setShowServiceSelection(false);
  };

  const handleBackToServiceSelection = () => {
    setShowServiceSelection(true);
    setServiceType(null);
    setError(null);
  };
  
  const handlePlaceToggle = (place: string) => {
    if (place === 'other') {
      setShowOtherPlaces(!showOtherPlaces);
    } else {
      setPlacesToVisit(prev =>
        prev.includes(place) ? prev.filter(p => p !== place) : [...prev, place]
      );
    }
  };
  
  const handleActivityToggle = (activityId: string) => {
    if (activityId === 'other') {
      setShowOtherActivities(!showOtherActivities);
    } else {
      setActivitiesToDo(prev =>
        prev.includes(activityId) ? prev.filter(a => a !== activityId) : [...prev, activityId]
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 bg-gradient-to-br from-purple-100 via-blue-50 to-purple-50" style={{ overflow: 'hidden' }}>
      <style>{`
        /* Prevent iOS zoom on input focus */
        @media (max-width: 767px) {
          input[type="text"],
          input[type="email"],
          input[type="date"],
          input[type="time"],
          input[type="number"],
          textarea,
          select {
            font-size: 16px !important;
          }
        }
      `}</style>
      <div className="relative w-full h-full max-w-4xl bg-white md:rounded-3xl shadow-2xl flex flex-col" style={{ maxHeight: '100vh', overflow: 'hidden' }}>
        {/* Header - Premium */}
        <div className="relative p-4 md:p-6 bg-gradient-to-br from-purple-400 via-purple-500 to-blue-500 text-white overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12"></div>
          
          <div className="relative flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg md:text-2xl font-bold truncate mb-1">Request Your Quote</h2>
              <p className="text-xs md:text-sm text-purple-100 truncate">{tripName}</p>
            </div>
            <button
              onClick={onClose}
              className="ml-2 p-2 md:p-2.5 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all flex-shrink-0"
              disabled={isSubmitting}
            >
              <X className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>
        </div>

        {/* Form Content - Premium */}
        <div className="flex-1 overflow-y-auto bg-gradient-to-br from-purple-50 via-white to-blue-50" style={{ minHeight: 0, maxHeight: '100%', contain: 'strict', position: 'relative' }}>
          <div className="p-3 md:p-6" style={{ minHeight: 0, maxHeight: '100%' }}>
          {/* Service Type Selection */}
          {showServiceSelection && (
            <div className="max-w-3xl mx-auto space-y-4 md:space-y-6">
              <div className="text-center mb-4 md:mb-6">
                <h3 className="text-lg md:text-2xl font-bold text-gray-800 mb-2">What can we help you with?</h3>
                <p className="text-sm md:text-base text-gray-600">Choose the service that best fits your needs</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Option A: Full Trip Planning */}
                <button
                  type="button"
                  onClick={() => handleServiceTypeSelect('full_trip')}
                  className="group relative bg-white rounded-2xl p-6 md:p-8 shadow-lg border-2 border-purple-100 hover:border-purple-400 transition-all hover:shadow-xl"
                >
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Map className="w-8 h-8 md:w-10 md:h-10 text-white" />
                    </div>
                    <div>
                      <h4 className="text-base md:text-xl font-bold text-gray-800 mb-2">Plan my whole trip</h4>
                      <p className="text-xs md:text-sm text-gray-600">Get clear costs for hotels, transport, experiences and attractions</p>
                    </div>
                  </div>
                </button>

                {/* Option B: Driver Only */}
                <button
                  type="button"
                  onClick={() => handleServiceTypeSelect('driver_only')}
                  className="group relative bg-white rounded-2xl p-6 md:p-8 shadow-lg border-2 border-purple-100 hover:border-purple-400 transition-all hover:shadow-xl"
                >
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Car className="w-8 h-8 md:w-10 md:h-10 text-white" />
                    </div>
                    <div>
                      <h4 className="text-base md:text-xl font-bold text-gray-800 mb-2">I just need a driver</h4>
                      <p className="text-xs md:text-sm text-gray-600">Just need reliable transport with clear costs</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Full Trip Form */}
          {!showServiceSelection && serviceType === 'full_trip' && (
            <form onSubmit={handleSubmit} className="max-w-3xl mx-auto space-y-3 md:space-y-4" style={{ contain: 'layout' }}>
              {/* Back Button */}
              <button
                type="button"
                onClick={handleBackToServiceSelection}
                className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold text-sm md:text-base mb-2"
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to service selection
              </button>

              {/* Section 1: Travel Status - Premium Card */}
            <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
              <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                <span className="text-purple-500">✈️</span> Travel Status
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <label className={`relative flex items-center justify-center p-3 md:p-4 rounded-xl cursor-pointer transition-all ${
                  travelStatus === 'visiting_soon' 
                    ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-lg' 
                    : 'bg-purple-50 text-gray-700 hover:bg-purple-100'
                }`}>
                  <input
                    type="radio"
                    name="travelStatus"
                    value="visiting_soon"
                    checked={travelStatus === 'visiting_soon'}
                    onChange={(e) => setTravelStatus(e.target.value as TravelStatus)}
                    className="sr-only"
                  />
                  <span className="text-xs md:text-sm font-semibold">Visiting Soon</span>
                </label>
                <label className={`relative flex items-center justify-center p-3 md:p-4 rounded-xl cursor-pointer transition-all ${
                  travelStatus === 'already_in_sri_lanka' 
                    ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-lg' 
                    : 'bg-purple-50 text-gray-700 hover:bg-purple-100'
                }`}>
                  <input
                    type="radio"
                    name="travelStatus"
                    value="already_in_sri_lanka"
                    checked={travelStatus === 'already_in_sri_lanka'}
                    onChange={(e) => setTravelStatus(e.target.value as TravelStatus)}
                    className="sr-only"
                  />
                  <span className="text-xs md:text-sm font-semibold">In Sri Lanka</span>
                </label>
              </div>
            </div>

            {/* Section 2A: Visiting Soon - Dates - Premium Card */}
            {travelStatus === 'visiting_soon' && (
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">📅</span> Travel Dates
                </h3>
                <div className="space-y-3 md:space-y-4">
                  <div className="bg-purple-50 rounded-xl p-3">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Arrival Date & Time
                    </label>
                    <div className="flex gap-2 items-center">
                      {isLazyMode ? (
                        <input
                          type="date"
                          value={arrivalDate}
                          onChange={(e) => setArrivalDate(e.target.value)}
                          min={getMinDate()}
                          className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                        />
                      ) : (
                        <div className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-gray-100 rounded-xl text-gray-600">
                          {startDateObj.toLocaleDateString()}
                        </div>
                      )}
                      <input
                        type="time"
                        value={arrivalTime}
                        onChange={(e) => setArrivalTime(e.target.value)}
                        className="w-24 md:w-32 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                      />
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Departure Date & Time
                    </label>
                    <div className="flex gap-2 items-center">
                      {isLazyMode ? (
                        <input
                          type="date"
                          value={departureDate}
                          onChange={(e) => setDepartureDate(e.target.value)}
                          min={arrivalDate || getMinDate()}
                          className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                        />
                      ) : (
                        <div className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-gray-100 rounded-xl text-gray-600">
                          {endDateObj.toLocaleDateString()}
                        </div>
                      )}
                      <input
                        type="time"
                        value={departureTime}
                        onChange={(e) => setDepartureTime(e.target.value)}
                        className="w-24 md:w-32 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 2B: Already in Sri Lanka - Pickup/Dropoff - Premium Card */}
            {travelStatus === 'already_in_sri_lanka' && (
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">🕐</span> Pickup & Drop-off
                </h3>
                <div className="space-y-3 md:space-y-4">
                  <div className="bg-purple-50 rounded-xl p-3">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Pickup Date & Time
                    </label>
                    <div className="flex gap-2 items-center">
                      {isLazyMode ? (
                        <input
                          type="date"
                          value={arrivalDate}
                          onChange={(e) => setArrivalDate(e.target.value)}
                          min={getMinDate()}
                          className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                        />
                      ) : (
                        <div className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-gray-100 rounded-xl text-gray-600">
                          {startDateObj.toLocaleDateString()}
                        </div>
                      )}
                      <input
                        type="time"
                        value={pickupTime}
                        onChange={(e) => setPickupTime(e.target.value)}
                        className="w-24 md:w-32 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                      />
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Drop-off Date & Time
                    </label>
                    <div className="flex gap-2 items-center">
                      {isLazyMode ? (
                        <input
                          type="date"
                          value={departureDate}
                          onChange={(e) => setDepartureDate(e.target.value)}
                          min={arrivalDate || getMinDate()}
                          className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                        />
                      ) : (
                        <div className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-gray-100 rounded-xl text-gray-600">
                          {endDateObj.toLocaleDateString()}
                        </div>
                      )}
                      <input
                        type="time"
                        value={dropoffTime}
                        onChange={(e) => setDropoffTime(e.target.value)}
                        className="w-24 md:w-32 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 3: Traveler Details - Premium Card */}
            <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
              <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                <span className="text-purple-500">👥</span> Traveler Details
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-purple-50 rounded-xl p-3">
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                    Adults
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={adults}
                    onChange={(e) => setAdults(parseInt(e.target.value) || 0)}
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                  />
                </div>
                <div className="bg-purple-50 rounded-xl p-3">
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                    Children
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={children}
                    onChange={(e) => setChildren(parseInt(e.target.value) || 0)}
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                  />
                </div>
                <div className="bg-purple-50 rounded-xl p-3">
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                    Infants
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={infants}
                    onChange={(e) => setInfants(parseInt(e.target.value) || 0)}
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Section 3.5: Places to Visit (Lazy Mode Only) */}
            {isLazyMode && (
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">📍</span> I wish to visit
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {PLACES_TO_VISIT.map((place) => (
                    <label key={place} className={`flex items-center cursor-pointer p-2.5 md:p-3 rounded-xl transition-all ${
                      placesToVisit.includes(place)
                        ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-md'
                        : 'bg-purple-50 hover:bg-purple-100'
                    }`}>
                      <input
                        type="checkbox"
                        checked={placesToVisit.includes(place)}
                        onChange={() => handlePlaceToggle(place)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 md:w-5 md:h-5 rounded-md mr-2 flex items-center justify-center ${
                        placesToVisit.includes(place) ? 'bg-white' : 'bg-white border-2 border-purple-300'
                      }`}>
                        {placesToVisit.includes(place) && (
                          <svg className="w-3 h-3 md:w-4 md:h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className={`text-xs md:text-sm font-medium ${placesToVisit.includes(place) ? 'text-white' : 'text-gray-700'}`}>
                        {place}
                      </span>
                    </label>
                  ))}
                  <label className={`flex items-center cursor-pointer p-2.5 md:p-3 rounded-xl transition-all ${
                    showOtherPlaces
                      ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-md'
                      : 'bg-purple-50 hover:bg-purple-100'
                  }`}>
                    <input
                      type="checkbox"
                      checked={showOtherPlaces}
                      onChange={() => handlePlaceToggle('other')}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 md:w-5 md:h-5 rounded-md mr-2 flex items-center justify-center ${
                      showOtherPlaces ? 'bg-white' : 'bg-white border-2 border-purple-300'
                    }`}>
                      {showOtherPlaces && (
                        <svg className="w-3 h-3 md:w-4 md:h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-xs md:text-sm font-medium ${showOtherPlaces ? 'text-white' : 'text-gray-700'}`}>
                      Other places
                    </span>
                  </label>
                </div>
                <div style={{ 
                  height: showOtherPlaces ? 'auto' : '0',
                  overflow: 'hidden',
                  transition: 'height 0.2s ease'
                }}>
                  <textarea
                    value={otherPlacesText}
                    onChange={(e) => setOtherPlacesText(e.target.value)}
                    placeholder="Specify other places..."
                    rows={2}
                    className="w-full mt-3 px-4 py-3 text-xs md:text-sm border-0 rounded-xl focus:ring-2 focus:ring-purple-400 resize-none bg-purple-50 shadow-sm"
                  />
                </div>
              </div>
            )}

            {/* Section 3.6: Activities (Lazy Mode Only) */}
            {isLazyMode && (
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">🎯</span> I want to do
                </h3>
                <div className="grid grid-cols-2 gap-2" style={{ gridAutoRows: 'auto' }}>
                  {ACTIVITIES_TO_DO.map((activity) => (
                    <label key={activity.id} className={`flex items-center cursor-pointer p-2.5 md:p-3 rounded-xl transition-all ${
                      activitiesToDo.includes(activity.id)
                        ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-md'
                        : 'bg-purple-50 hover:bg-purple-100'
                    }`}>
                      <input
                        type="checkbox"
                        checked={activitiesToDo.includes(activity.id)}
                        onChange={() => handleActivityToggle(activity.id)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 md:w-5 md:h-5 rounded-md mr-2 flex items-center justify-center ${
                        activitiesToDo.includes(activity.id) ? 'bg-white' : 'bg-white border-2 border-purple-300'
                      }`}>
                        {activitiesToDo.includes(activity.id) && (
                          <svg className="w-3 h-3 md:w-4 md:h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className={`text-xs md:text-sm font-medium ${activitiesToDo.includes(activity.id) ? 'text-white' : 'text-gray-700'}`}>
                        {activity.label}
                      </span>
                    </label>
                  ))}
                  <label className={`flex items-center cursor-pointer p-2.5 md:p-3 rounded-xl transition-all ${
                    showOtherActivities
                      ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-md'
                      : 'bg-purple-50 hover:bg-purple-100'
                  }`}>
                    <input
                      type="checkbox"
                      checked={showOtherActivities}
                      onChange={() => handleActivityToggle('other')}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 md:w-5 md:h-5 rounded-md mr-2 flex items-center justify-center ${
                      showOtherActivities ? 'bg-white' : 'bg-white border-2 border-purple-300'
                    }`}>
                      {showOtherActivities && (
                        <svg className="w-3 h-3 md:w-4 md:h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-xs md:text-sm font-medium ${showOtherActivities ? 'text-white' : 'text-gray-700'}`}>
                      Other
                    </span>
                  </label>
                </div>
                <div style={{ 
                  height: showOtherActivities ? 'auto' : '0',
                  overflow: 'hidden',
                  transition: 'height 0.2s ease'
                }}>
                  <textarea
                    value={otherActivitiesText}
                    onChange={(e) => setOtherActivitiesText(e.target.value)}
                    placeholder="Specify other activities..."
                    rows={2}
                    className="w-full mt-3 px-4 py-3 text-xs md:text-sm border-0 rounded-xl focus:ring-2 focus:ring-purple-400 resize-none bg-purple-50 shadow-sm"
                  />
                </div>
              </div>
            )}

            {/* Section 4: My Style - Premium Card */}
            <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
              <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                <span className="text-purple-500">🎨</span> My Style
              </h3>
              <div className="space-y-2 md:space-y-2.5">
                {TRAVEL_STYLES.map((style) => (
                  <div key={style.id} className="rounded-xl overflow-hidden">
                    <label className={`flex items-start p-3 md:p-3.5 cursor-pointer transition-all rounded-xl ${
                      travelStyle === style.id 
                        ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-md' 
                        : 'bg-purple-50 hover:bg-purple-100'
                    }`}>
                      <input
                        type="radio"
                        name="travelStyle"
                        value={style.id}
                        checked={travelStyle === style.id}
                        onChange={(e) => setTravelStyle(e.target.value as TravelStyle)}
                        className="sr-only"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs md:text-sm font-bold truncate ${travelStyle === style.id ? 'text-white' : 'text-gray-800'}`}>
                              {style.title}
                            </p>
                            <p className={`text-xs mt-1 line-clamp-1 ${travelStyle === style.id ? 'text-purple-100' : 'text-gray-600'}`}>
                              {style.shortDesc}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setExpandedTravelStyle(expandedTravelStyle === style.id ? null : style.id)}
                            className={`p-1 rounded-lg flex-shrink-0 ${travelStyle === style.id ? 'text-white' : 'text-gray-400 hover:text-gray-600'}`}
                          >
                            {expandedTravelStyle === style.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                        {expandedTravelStyle === style.id && (
                          <p className={`text-xs mt-2 pt-2 border-t ${travelStyle === style.id ? 'text-purple-50 border-purple-300' : 'text-gray-500 border-purple-200'}`}>
                            {style.fullDesc}
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 4: Budget Style - Premium Card */}
            <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
              <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                <span className="text-purple-500">💰</span> Budget Style
              </h3>
              <div className="space-y-2 md:space-y-2.5">
                {BUDGET_STYLES.map((style) => (
                  <div key={style.id} className="rounded-xl overflow-hidden">
                    <label className={`flex items-start p-3 md:p-3.5 cursor-pointer transition-all rounded-xl ${
                      budgetStyle === style.id 
                        ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-md' 
                        : 'bg-purple-50 hover:bg-purple-100'
                    }`}>
                      <input
                        type="radio"
                        name="budgetStyle"
                        value={style.id}
                        checked={budgetStyle === style.id}
                        onChange={(e) => setBudgetStyle(e.target.value as BudgetStyle)}
                        className="sr-only"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs md:text-sm font-bold truncate ${budgetStyle === style.id ? 'text-white' : 'text-gray-800'}`}>
                              {style.title}
                            </p>
                            <p className={`text-xs mt-1 line-clamp-1 ${budgetStyle === style.id ? 'text-purple-100' : 'text-gray-600'}`}>
                              {style.shortDesc}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setExpandedBudgetStyle(expandedBudgetStyle === style.id ? null : style.id)}
                            className={`p-1 rounded-lg flex-shrink-0 ${budgetStyle === style.id ? 'text-white' : 'text-gray-400 hover:text-gray-600'}`}
                          >
                            {expandedBudgetStyle === style.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                        {expandedBudgetStyle === style.id && (
                          <p className={`text-xs mt-2 pt-2 border-t ${budgetStyle === style.id ? 'text-purple-50 border-purple-300' : 'text-gray-500 border-purple-200'}`}>
                            {style.fullDesc}
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 5: Language Preference - Premium Card */}
            <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
              <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                <span className="text-purple-500">🌍</span> Language
              </h3>
              <select
                value={languagePreference}
                onChange={(e) => setLanguagePreference(e.target.value)}
                className="w-full px-4 py-3 text-xs md:text-sm border-0 rounded-xl focus:ring-2 focus:ring-purple-400 bg-purple-50 font-medium shadow-sm"
              >
                {LANGUAGE_OPTIONS.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Section 6: Additional Notes - Premium Card */}
            <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
              <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                <span className="text-purple-500">📝</span> Additional Notes
              </h3>
              <textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="Anything else you want us to know?"
                className="w-full px-4 py-3 text-xs md:text-sm border-0 rounded-xl focus:ring-2 focus:ring-purple-400 resize-none bg-purple-50 shadow-sm"
              />
              <p className="text-xs text-gray-500 mt-2 text-right font-medium">
                {additionalNotes.length}/1000
              </p>
            </div>

            {/* Section 7: Terms & Disclaimers - Premium Card */}
            <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
              <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                <span className="text-purple-500">✓</span> Terms & Conditions
              </h3>
              <div className="space-y-2.5 md:space-y-3">
                {TERMS_AND_CONDITIONS.map((term, index) => {
                  // For lazy mode, only show the last term (index 2)
                  if (isLazyMode && index !== 2) return null;
                  
                  return (
                    <label key={index} className={`flex items-start cursor-pointer p-3 md:p-3.5 rounded-xl transition-all ${
                      termsAccepted[index as 0 | 1 | 2]
                        ? 'bg-gradient-to-br from-purple-400 to-purple-500 shadow-md'
                        : 'bg-purple-50 hover:bg-purple-100'
                    }`}>
                      <div className="relative flex-shrink-0 mt-0.5">
                        <input
                          type="checkbox"
                          checked={termsAccepted[index as 0 | 1 | 2]}
                          onChange={() => handleTermsChange(index as 0 | 1 | 2)}
                          className="peer sr-only"
                        />
                        <div className={`w-5 h-5 md:w-6 md:h-6 rounded-lg transition-all flex items-center justify-center ${
                          termsAccepted[index as 0 | 1 | 2]
                            ? 'bg-white'
                            : 'bg-white border-2 border-purple-300'
                        }`}>
                          {termsAccepted[index as 0 | 1 | 2] && (
                            <svg className="w-4 h-4 md:w-5 md:h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className={`ml-3 text-xs md:text-sm leading-snug ${
                        termsAccepted[index as 0 | 1 | 2] ? 'text-white font-medium' : 'text-gray-700'
                      }`}>{term}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Section 8: Communication Method - Premium Card */}
            <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
              <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                <span className="text-purple-500">📱</span> Contact Method
              </h3>
              <div className="grid grid-cols-3 gap-2 md:gap-3">
                <button
                  type="button"
                  onClick={() => setCommunicationMethod('email')}
                  className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-xl transition-all ${
                    communicationMethod === 'email'
                      ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-md'
                      : 'bg-purple-50 hover:bg-purple-100'
                  }`}
                >
                  <Mail className={`w-6 h-6 md:w-7 md:h-7 mb-1.5 ${communicationMethod === 'email' ? 'text-white' : 'text-purple-500'}`} />
                  <span className={`text-xs md:text-sm font-semibold ${communicationMethod === 'email' ? 'text-white' : 'text-gray-700'}`}>Email</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCommunicationMethod('whatsapp')}
                  className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-xl transition-all ${
                    communicationMethod === 'whatsapp'
                      ? 'bg-gradient-to-br from-green-400 to-green-500 text-white shadow-md'
                      : 'bg-green-50 hover:bg-green-100'
                  }`}
                >
                  <MessageCircle className={`w-6 h-6 md:w-7 md:h-7 mb-1.5 ${communicationMethod === 'whatsapp' ? 'text-white' : 'text-green-500'}`} />
                  <span className={`text-xs md:text-sm font-semibold ${communicationMethod === 'whatsapp' ? 'text-white' : 'text-gray-700'}`}>WhatsApp</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCommunicationMethod('telegram')}
                  className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-xl transition-all ${
                    communicationMethod === 'telegram'
                      ? 'bg-gradient-to-br from-blue-400 to-blue-500 text-white shadow-md'
                      : 'bg-blue-50 hover:bg-blue-100'
                  }`}
                >
                  <Send className={`w-6 h-6 md:w-7 md:h-7 mb-1.5 ${communicationMethod === 'telegram' ? 'text-white' : 'text-blue-500'}`} />
                  <span className={`text-xs md:text-sm font-semibold ${communicationMethod === 'telegram' ? 'text-white' : 'text-gray-700'}`}>Telegram</span>
                </button>
              </div>
            </div>

            {/* Error Message - Premium */}
            {error && (
              <div className="bg-red-50 rounded-2xl p-4 shadow-lg border border-red-200">
                <p className="text-red-600 text-xs md:text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Submit Buttons - Inside Form */}
            <div className="flex gap-3 justify-end pt-2 pb-32 md:pb-0">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-5 md:px-8 py-2.5 md:py-3 text-xs md:text-sm bg-white border-2 border-purple-200 rounded-xl text-gray-700 hover:bg-purple-50 transition-all disabled:opacity-50 font-semibold shadow-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid() || isSubmitting}
                className="px-6 md:px-10 py-2.5 md:py-3 text-xs md:text-sm bg-gradient-to-br from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-bold shadow-lg"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Request Quote'
                )}
              </button>
            </div>
            </form>
          )}

          {/* Driver-Only Form */}
          {!showServiceSelection && serviceType === 'driver_only' && (
            <form onSubmit={handleSubmit} className="max-w-3xl mx-auto space-y-3 md:space-y-4">
              {/* Back Button */}
              <button
                type="button"
                onClick={handleBackToServiceSelection}
                className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold text-sm md:text-base mb-2"
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to service selection
              </button>

              {/* Section 1: Travel Status */}
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">✈️</span> Travel Status
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <label className={`relative flex items-center justify-center p-3 md:p-4 rounded-xl cursor-pointer transition-all ${
                    travelStatus === 'visiting_soon' 
                      ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-lg' 
                      : 'bg-purple-50 text-gray-700 hover:bg-purple-100'
                  }`}>
                    <input
                      type="radio"
                      name="driverTravelStatus"
                      value="visiting_soon"
                      checked={travelStatus === 'visiting_soon'}
                      onChange={(e) => setTravelStatus(e.target.value as TravelStatus)}
                      className="sr-only"
                    />
                    <span className="text-xs md:text-sm font-semibold">Visiting Soon</span>
                  </label>
                  <label className={`relative flex items-center justify-center p-3 md:p-4 rounded-xl cursor-pointer transition-all ${
                    travelStatus === 'already_in_sri_lanka' 
                      ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-lg' 
                      : 'bg-purple-50 text-gray-700 hover:bg-purple-100'
                  }`}>
                    <input
                      type="radio"
                      name="driverTravelStatus"
                      value="already_in_sri_lanka"
                      checked={travelStatus === 'already_in_sri_lanka'}
                      onChange={(e) => setTravelStatus(e.target.value as TravelStatus)}
                      className="sr-only"
                    />
                    <span className="text-xs md:text-sm font-semibold">In Sri Lanka</span>
                  </label>
                </div>
              </div>

              {/* Section 2: Travel Dates/Times */}
              {travelStatus === 'visiting_soon' ? (
                <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                  <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                    <span className="text-purple-500">📅</span> Travel Dates
                  </h3>
                  <div className="space-y-3 md:space-y-4">
                    <div className="bg-purple-50 rounded-xl p-3">
                      <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                        Arrival Date & Time
                      </label>
                      <div className="flex gap-2 items-center">
                        {isLazyMode ? (
                          <input
                            type="date"
                            value={arrivalDate}
                            onChange={(e) => setArrivalDate(e.target.value)}
                            min={getMinDate()}
                            className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                          />
                        ) : (
                          <div className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-gray-100 rounded-xl text-gray-600">
                            {startDateObj.toLocaleDateString()}
                          </div>
                        )}
                        <input
                          type="time"
                          value={arrivalTime}
                          onChange={(e) => setArrivalTime(e.target.value)}
                          className="w-24 md:w-32 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                        />
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-3">
                      <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                        Departure Date & Time
                      </label>
                      <div className="flex gap-2 items-center">
                        {isLazyMode ? (
                          <input
                            type="date"
                            value={departureDate}
                            onChange={(e) => setDepartureDate(e.target.value)}
                            min={arrivalDate || getMinDate()}
                            className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                          />
                        ) : (
                          <div className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-gray-100 rounded-xl text-gray-600">
                            {endDateObj.toLocaleDateString()}
                          </div>
                        )}
                        <input
                          type="time"
                          value={departureTime}
                          onChange={(e) => setDepartureTime(e.target.value)}
                          className="w-24 md:w-32 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                  <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                    <span className="text-purple-500">🕐</span> Pickup & Drop-off
                  </h3>
                  <div className="space-y-3 md:space-y-4">
                    <div className="bg-purple-50 rounded-xl p-3">
                      <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                        Pickup Date & Time
                      </label>
                      <div className="flex gap-2 items-center">
                        {isLazyMode ? (
                          <input
                            type="date"
                            value={arrivalDate}
                            onChange={(e) => setArrivalDate(e.target.value)}
                            min={getMinDate()}
                            className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                          />
                        ) : (
                          <div className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-gray-100 rounded-xl text-gray-600">
                            {startDateObj.toLocaleDateString()}
                          </div>
                        )}
                        <input
                          type="time"
                          value={pickupTime}
                          onChange={(e) => setPickupTime(e.target.value)}
                          className="w-24 md:w-32 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                        />
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-3">
                      <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                        Drop-off Date & Time
                      </label>
                      <div className="flex gap-2 items-center">
                        {isLazyMode ? (
                          <input
                            type="date"
                            value={departureDate}
                            onChange={(e) => setDepartureDate(e.target.value)}
                            min={arrivalDate || getMinDate()}
                            className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                          />
                        ) : (
                          <div className="flex-1 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-gray-100 rounded-xl text-gray-600">
                            {endDateObj.toLocaleDateString()}
                          </div>
                        )}
                        <input
                          type="time"
                          value={dropoffTime}
                          onChange={(e) => setDropoffTime(e.target.value)}
                          className="w-24 md:w-32 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Section 3: Traveler Details */}
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">👥</span> Traveler Details
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-purple-50 rounded-xl p-3">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Adults
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="20"
                      value={adults}
                      onChange={(e) => setAdults(parseInt(e.target.value) || 0)}
                      className="w-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                    />
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Children
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="20"
                      value={children}
                      onChange={(e) => setChildren(parseInt(e.target.value) || 0)}
                      className="w-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                    />
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Infants
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={infants}
                      onChange={(e) => setInfants(parseInt(e.target.value) || 0)}
                      className="w-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-white border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3.5: Rough Itinerary (Lazy Mode Only) */}
              {isLazyMode && (
                <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                  <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                    <span className="text-purple-500">📝</span> This is my itinerary
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 mb-3">
                    Doesn't have to be finalized, we can help you with it. Just jot down what you wanna do and places you may have in mind.
                  </p>
                  <textarea
                    value={roughItinerary}
                    onChange={(e) => setRoughItinerary(e.target.value)}
                    placeholder="e.g., Day 1: Arrive Colombo, Day 2: Drive to Galle..."
                    rows={5}
                    className="w-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-purple-50 border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm resize-none"
                  />
                </div>
              )}

              {/* Section 4: Vehicle Type */}
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">🚗</span> Required Vehicle Type
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {VEHICLE_TYPES.map((vehicle) => (
                    <label key={vehicle.id} className={`relative flex flex-col items-center justify-center p-3 md:p-4 rounded-xl cursor-pointer transition-all ${
                      vehicleType === vehicle.id 
                        ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-lg' 
                        : 'bg-purple-50 text-gray-700 hover:bg-purple-100'
                    }`}>
                      <input
                        type="radio"
                        name="vehicleType"
                        value={vehicle.id}
                        checked={vehicleType === vehicle.id}
                        onChange={(e) => setVehicleType(e.target.value as VehicleType)}
                        className="sr-only"
                      />
                      <span className="text-xs md:text-sm font-bold">{vehicle.title}</span>
                      <span className={`text-xs mt-1 ${vehicleType === vehicle.id ? 'text-purple-100' : 'text-gray-500'}`}>
                        {vehicle.capacity}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Section 5: Special Needs */}
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">♿</span> Special Needs
                </h3>
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={hasSpecialNeeds}
                      onChange={(e) => setHasSpecialNeeds(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 md:w-6 md:h-6 border-2 border-purple-300 rounded-md peer-checked:bg-purple-500 peer-checked:border-purple-500 transition-all flex items-center justify-center">
                      {hasSpecialNeeds && (
                        <svg className="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-xs md:text-sm font-semibold text-gray-700">I have travelers with special needs</span>
                </label>
                {hasSpecialNeeds && (
                  <textarea
                    value={specialNeedsDetails}
                    onChange={(e) => setSpecialNeedsDetails(e.target.value)}
                    placeholder="Please share more details about the special needs..."
                    maxLength={500}
                    rows={3}
                    className="mt-3 w-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-purple-50 border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm resize-none"
                  />
                )}
              </div>

              {/* Section 6: Additional Information */}
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">📝</span> Additional Information
                </h3>
                <textarea
                  value={driverAdditionalNotes}
                  onChange={(e) => setDriverAdditionalNotes(e.target.value)}
                  placeholder="Anything else we should be aware of?"
                  maxLength={1000}
                  rows={4}
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-purple-50 border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm resize-none"
                />
              </div>

              {/* Section 7: Language Preference */}
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">🌐</span> Language Preference
                </h3>
                <select
                  value={driverLanguagePreference}
                  onChange={(e) => setDriverLanguagePreference(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm bg-purple-50 border-0 rounded-xl focus:ring-2 focus:ring-purple-400 shadow-sm"
                >
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Section 8: Terms & Conditions */}
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">📋</span> Terms & Conditions
                </h3>
                <label className="flex items-start gap-3 cursor-pointer bg-purple-50 p-3 rounded-xl">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={driverTermsAccepted}
                      onChange={(e) => setDriverTermsAccepted(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 md:w-6 md:h-6 border-2 border-purple-300 rounded-md peer-checked:bg-purple-500 peer-checked:border-purple-500 transition-all flex items-center justify-center">
                      {driverTermsAccepted && (
                        <svg className="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-xs md:text-sm text-gray-700">
                    {TERMS_AND_CONDITIONS[2]}
                  </span>
                </label>
              </div>

              {/* Section 9: Communication Method */}
              <div className="bg-white rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100">
                <h3 className="text-sm md:text-base font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-purple-500">💬</span> How should we contact you?
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setDriverCommunicationMethod('email')}
                    className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-xl transition-all ${
                      driverCommunicationMethod === 'email' 
                        ? 'bg-gradient-to-br from-purple-400 to-purple-500 text-white shadow-lg' 
                        : 'bg-purple-50 text-gray-700 hover:bg-purple-100'
                    }`}
                  >
                    <Mail className="w-5 h-5 md:w-6 md:h-6 mb-1 md:mb-2" />
                    <span className="text-xs md:text-sm font-semibold">Email</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDriverCommunicationMethod('whatsapp')}
                    className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-xl transition-all ${
                      driverCommunicationMethod === 'whatsapp' 
                        ? 'bg-gradient-to-br from-green-400 to-green-500 text-white shadow-lg' 
                        : 'bg-purple-50 hover:bg-purple-100'
                    }`}
                  >
                    <MessageCircle className={`w-5 h-5 md:w-6 md:h-6 mb-1 md:mb-2 ${driverCommunicationMethod === 'whatsapp' ? 'text-white' : 'text-green-500'}`} />
                    <span className={`text-xs md:text-sm font-semibold ${driverCommunicationMethod === 'whatsapp' ? 'text-white' : 'text-gray-700'}`}>WhatsApp</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDriverCommunicationMethod('telegram')}
                    className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-xl transition-all ${
                      driverCommunicationMethod === 'telegram' 
                        ? 'bg-gradient-to-br from-blue-400 to-blue-500 text-white shadow-lg' 
                        : 'bg-purple-50 hover:bg-purple-100'
                    }`}
                  >
                    <Send className={`w-5 h-5 md:w-6 md:h-6 mb-1 md:mb-2 ${driverCommunicationMethod === 'telegram' ? 'text-white' : 'text-blue-500'}`} />
                    <span className={`text-xs md:text-sm font-semibold ${driverCommunicationMethod === 'telegram' ? 'text-white' : 'text-gray-700'}`}>Telegram</span>
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 rounded-2xl p-4 shadow-lg border border-red-200">
                  <p className="text-red-600 text-xs md:text-sm font-medium">{error}</p>
                </div>
              )}

              {/* Submit Buttons - Inside Form */}
              <div className="flex gap-3 justify-end pt-2 pb-32 md:pb-0">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-5 md:px-8 py-2.5 md:py-3 text-xs md:text-sm bg-white border-2 border-purple-200 rounded-xl text-gray-700 hover:bg-purple-50 transition-all disabled:opacity-50 font-semibold shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid() || isSubmitting}
                  className="px-6 md:px-10 py-2.5 md:py-3 text-xs md:text-sm bg-gradient-to-br from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-bold shadow-lg"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Request Quote'
                  )}
                </button>
              </div>
            </form>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteRequestForm;

